// ============================================================================
// BOQ — conceptual space-type area-rate view
// ============================================================================
// Browse the all-in area-rate corpus (boq.area_rates) to prime a NEW project's
// SD/concept estimate. Grouped by (building typology x space type) with the
// TYPOLOGY shown first and prominently, so the category is visible while
// selecting a rate. Escalation/median reuse the subtrade corpus maths.
// ============================================================================

import React, { useMemo, useState } from 'react';
import { Trash2, ChevronRight, ChevronDown } from 'lucide-react';
import {
  BrioCard, BrioSelect, BrioEmptyState, BrioInfoBanner,
} from '../../components/BrioComponents';
import { useBoqAreaRates } from '../hooks/useBoqAreaRates';
import { summarizeRates, spreadDisplay } from '../utils/sdRateStats';
import { SPACE_TYPE_LABELS, spaceTypeLabel, groupByTypologySpace } from '../utils/areaRateMeta';
import { StickyScrollBox } from './StickyScrollbar';

const fmt = (v) => (v == null ? '—' : `₹${Math.round(Number(v)).toLocaleString('en-IN')}`);
const cell = { padding: '0.5rem 0.65rem', fontSize: '0.85rem', borderBottom: '1px solid var(--brio-border-subtle, #eee)' };
const th = { ...cell, textAlign: 'left', fontWeight: 600, color: 'var(--brio-text-muted)', textTransform: 'uppercase', fontSize: '0.72rem', letterSpacing: '0.03em', whiteSpace: 'nowrap' };
const numCell = { ...cell, textAlign: 'right', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' };

export default function AreaRatesView({ asOf, costIndex, typologies = [] }) {
  const api = useBoqAreaRates();
  const [typologyFilter, setTypologyFilter] = useState('all');
  const [spaceFilter, setSpaceFilter] = useState('all');
  const [expanded, setExpanded] = useState(null);

  const typologyLabelById = useMemo(
    () => new Map(typologies.map((t) => [t.id, t.label])),
    [typologies],
  );

  const rows = useMemo(() => {
    const filtered = api.rates.filter((r) => (
      (typologyFilter === 'all' || (typologyFilter === 'none' ? !r.typologyId : r.typologyId === typologyFilter))
      && (spaceFilter === 'all' || r.spaceType === spaceFilter)
    ));
    const groups = groupByTypologySpace(filtered);
    const out = [];
    groups.forEach((rates, key) => {
      const [typoId, spaceType] = key.split('::');
      const typologyLabel = typoId === 'none' ? '(no typology)' : (typologyLabelById.get(typoId) || rates[0]?.typologyLabel || '(typology)');
      const stats = summarizeRates(rates, {
        adjust: costIndex.adjust, factor: costIndex.factor, asOfDate: asOf,
      });
      out.push({ key, typoId, spaceType, typologyLabel, ...stats });
    });
    return out.sort((a, b) => a.typologyLabel.localeCompare(b.typologyLabel)
      || a.spaceType.localeCompare(b.spaceType));
  }, [api.rates, typologyFilter, spaceFilter, typologyLabelById, costIndex.adjust, costIndex.factor, asOf]);

  const onDelete = async (id) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm('Delete this area-rate from the corpus?')) return;
    await api.deleteMany([id]);
  };

  if (api.loading) {
    return <BrioCard><div style={{ padding: '1.5rem', color: 'var(--brio-text-muted)' }}>Loading area-rates…</div></BrioCard>;
  }
  if (api.error) {
    return <BrioCard><div style={{ padding: '1.5rem', color: 'var(--brio-danger, #b00)' }}>{api.error}</div></BrioCard>;
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: '1rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
        <label style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', fontSize: '0.78rem', color: 'var(--brio-text-muted)' }}>
          Typology
          <BrioSelect value={typologyFilter} onChange={(e) => setTypologyFilter(e.target.value)}>
            <option value="all">All typologies</option>
            {typologies.filter((t) => t.isActive).map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
            <option value="none">(no typology)</option>
          </BrioSelect>
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', fontSize: '0.78rem', color: 'var(--brio-text-muted)' }}>
          Space type
          <BrioSelect value={spaceFilter} onChange={(e) => setSpaceFilter(e.target.value)}>
            <option value="all">All space types</option>
            {Object.entries(SPACE_TYPE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </BrioSelect>
        </label>
      </div>

      <BrioInfoBanner>
        All-in conceptual rates per sqm of each space type, grouped by building typology and escalated to <strong>{asOf}</strong>.
        Pick a typology to prime a new project’s order-of-magnitude estimate.
        {!costIndex.hasData && ' ⚠ No cost-index data loaded — showing un-escalated rates.'}
      </BrioInfoBanner>

      {rows.length === 0 ? (
        <BrioCard>
          <BrioEmptyState>No area-rates for this filter yet.</BrioEmptyState>
        </BrioCard>
      ) : (
        <BrioCard>
          <StickyScrollBox style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={{ ...th, width: 28 }} />
                  <th style={th}>Typology</th>
                  <th style={th}>Space type</th>
                  <th style={{ ...th, textAlign: 'right' }}>n</th>
                  <th style={{ ...th, textAlign: 'right' }}>Projects</th>
                  <th style={{ ...th, textAlign: 'right' }}>Median /sqm</th>
                  <th style={{ ...th, textAlign: 'right' }}>p25–p75</th>
                  <th style={{ ...th, textAlign: 'right' }}>Range</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const isOpen = expanded === r.key;
                  const spread = spreadDisplay(r);
                  return (
                    <React.Fragment key={r.key}>
                      <tr
                        onClick={() => setExpanded(isOpen ? null : r.key)}
                        style={{ cursor: 'pointer', background: isOpen ? 'var(--brio-bg, #f7f7f5)' : 'transparent' }}
                      >
                        <td style={{ ...cell, textAlign: 'center', color: 'var(--brio-text-muted)' }}>
                          {isOpen ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
                        </td>
                        <td style={{ ...cell, fontWeight: 700, color: 'var(--brio-gold, #9F902A)' }}>{r.typologyLabel}</td>
                        <td style={cell}>{spaceTypeLabel(r.spaceType)}</td>
                        <td style={numCell}>{r.sampleSize}</td>
                        <td style={numCell}>{r.distinctProjects}</td>
                        <td style={{ ...numCell, fontWeight: 700 }}>{fmt(r.median)}</td>
                        <td style={{ ...numCell, color: 'var(--brio-text-muted)' }}>
                          {spread.quartiles ? `${fmt(spread.quartiles[0])}–${fmt(spread.quartiles[1])}` : <span style={{ fontStyle: 'italic', fontSize: '0.74rem' }}>{spread.note}</span>}
                        </td>
                        <td style={{ ...numCell, color: 'var(--brio-text-muted)' }}>
                          {spread.range ? `${fmt(spread.range[0])}–${fmt(spread.range[1])}` : '—'}
                        </td>
                      </tr>
                      {isOpen && (
                        <tr>
                          <td colSpan={8} style={{ padding: 0, background: 'var(--brio-bg, #f7f7f5)' }}>
                            <div style={{ padding: '0.5rem 1rem 0.9rem 2.5rem' }}>
                              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead>
                                  <tr>
                                    <th style={th}>Project</th>
                                    <th style={{ ...th, textAlign: 'right' }}>Original /sqm</th>
                                    <th style={{ ...th, textAlign: 'right' }}>Rate date</th>
                                    <th style={{ ...th, textAlign: 'right' }}>Escalated /sqm</th>
                                    <th style={{ ...th, width: 36 }} />
                                  </tr>
                                </thead>
                                <tbody>
                                  {r.contributors.map((c) => (
                                    <tr key={c.id}>
                                      <td style={cell}>
                                        {c.projectCode ? `${c.projectCode} — ${c.projectName || ''}` : '(no project)'}
                                        {c.zoneLabel && (
                                          <span style={{ color: 'var(--brio-text-muted)', fontSize: '0.72rem', marginLeft: 6 }}>{c.zoneLabel}</span>
                                        )}
                                      </td>
                                      <td style={numCell}>{fmt(c.rate)}</td>
                                      <td style={{ ...numCell, color: 'var(--brio-text-muted)' }}>{c.rateDate || '—'}</td>
                                      <td style={numCell}>{fmt(c.escalatedRate)}</td>
                                      <td style={{ ...cell, textAlign: 'center' }}>
                                        <button
                                          type="button"
                                          title="Delete rate"
                                          onClick={(e) => { e.stopPropagation(); onDelete(c.id); }}
                                          style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--brio-text-muted)' }}
                                        >
                                          <Trash2 size={14} />
                                        </button>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </StickyScrollBox>
        </BrioCard>
      )}
    </div>
  );
}
